/**
 * A SECTION IS A PLACE ON A HOUSE. A RUN IS THE LIGHTS ON IT.
 *
 * They are not the same record and the v0.2 contract is explicit about it: one
 * section can carry several design runs, and a physical length of wire is a
 * third thing again. Collapsing them is what makes a repair in February
 * impossible to describe - "the run above the garage" means nothing once
 * somebody has redrawn it.
 *
 * WHAT THIS FILE IS FOR, AND WHAT IT DELIBERATELY IS NOT.
 *
 * It is the spine: stable identity, a label that can change without moving
 * that identity, and a measurement that knows how it was arrived at. It is not
 * a detector, not a renderer and not a pricing engine. Every one of those
 * already exists and none of them is touched.
 *
 * THE ONE RULE THE WHOLE PHASE IS GATED ON. An uncertain measurement is never
 * silently treated as a verified one. A number read off a photograph, or
 * scaled from a reference dimension, is an estimate until a person stands at
 * the house with a wheel and says otherwise. Those are different states with
 * different words, and the estimate has to be able to say which is which,
 * because the difference between them is a job that loses money.
 *
 * NOTHING HERE IS DESTRUCTIVE. A document saved before sections existed opens
 * with an empty list and behaves exactly as it did. There is no customer
 * migration, no new customer table, and no identity keyed on a display label
 * or an array position - the three things the contract names as forbidden.
 */

/** How a length came to be known, in increasing order of trust. */
export type MeasureSource =
  /** Read off the drawing or typed from memory. A guess with a number on it. */
  | 'estimated'
  /** Scaled from a reference dimension somebody entered on this photo. */
  | 'calibrated'
  /** Somebody stood at the house and measured it. */
  | 'field'

/** What part of a roof this is. The trade's own words, not geometry. */
export type SectionKind =
  | 'eave'
  | 'rake'
  | 'ridge'
  | 'hip'
  | 'valley'
  | 'dormer'
  | 'other'

export interface Measurement {
  /**
   * Feet. Never derived silently from pixels: a photograph has no scale until
   * somebody gives it one, and a pixel length is not billable footage.
   */
  feet: number
  source: MeasureSource
  /** Plus or minus, in feet. Zero means a single number was given. */
  plusMinus: number
  /** Set only by a field measurement. Null on anything else, always. */
  verifiedAt: string | null
  /** Who stood there. Free text, because a crew is not an account. */
  verifiedBy: string
  /** Wheel, laser, tape, "counted the courses". Whatever was actually done. */
  evidence: string
}

/**
 * WHAT A LENGTH USED TO BE, AND WHO CHANGED IT.
 *
 * Required by REQ-03-11, and the reason is not paperwork. A crew measures 64
 * feet where the office estimated 48, the bid moves, and in March somebody
 * asks why the job cost what it cost. Without this the answer is a number with
 * no story behind it and the argument is unwinnable.
 *
 * It records the measurement being REPLACED, not the new one - the new one is
 * on the section where anybody can read it. Append only. Nothing in this file
 * removes an entry.
 */
export interface MeasureOverride {
  /** When the replacement was recorded. */
  at: string
  /** Who replaced it. Free text, same as a verifier. */
  by: string
  /** Why, in their words. Blank is allowed; a wrong reason is not. */
  reason: string
  /** The measurement as it stood before. */
  was: Measurement
}

export interface RoofSection {
  /**
   * Stable for the life of the property. Minted once and never rewritten -
   * not from the label, not from a position in this array. A section that is
   * renamed, redrawn or moved to another elevation is still this section, and
   * every quantity, reservation and repair that ever pointed at it still
   * points at it.
   */
  id: string
  /** What a person calls it. Changeable, and carries no identity. */
  label: string
  kind: SectionKind
  /**
   * The drawn runs that belong here, by their existing layer ids. This is the
   * join that makes one set of ids flow from the design into the materials and
   * into the estimate, which is the whole gate on this phase.
   */
  runIds: string[]
  /**
   * Bumped whenever the shape underneath changes. A verified length describes
   * a shape; change the shape and the verification is about something that no
   * longer exists.
   */
  geometryRev: number
  measurement: Measurement
  /**
   * A FINGERPRINT OF THE RUNS UNDERNEATH, so a redraw cannot go unnoticed.
   *
   * Empty on a section with nothing attached. Compared on every document
   * change; a difference is what bumps geometryRev and demotes a verification,
   * which is how a nudged corner stops silently reusing a length somebody
   * measured against the shape it used to be.
   */
  geometrySig: string
  /** Every length this section has had, oldest first. Never pruned. */
  history: MeasureOverride[]
  /** Left out of the estimate on purpose, with a reason a customer can read. */
  excluded: boolean
  excludedReason: string
}

export const SECTION_KINDS: { id: SectionKind; label: string }[] = [
  { id: 'eave', label: 'Eave' },
  { id: 'rake', label: 'Rake' },
  { id: 'ridge', label: 'Ridge' },
  { id: 'hip', label: 'Hip' },
  { id: 'valley', label: 'Valley' },
  { id: 'dormer', label: 'Dormer' },
  { id: 'other', label: 'Other' },
]

export const SOURCE_LABEL: Record<MeasureSource, string> = {
  estimated: 'Estimated',
  calibrated: 'Scaled from a reference',
  field: 'Measured at the house',
}

export const emptyMeasurement = (): Measurement => ({
  feet: 0,
  source: 'estimated',
  plusMinus: 0,
  verifiedAt: null,
  verifiedBy: '',
  evidence: '',
})

/**
 * VERIFIED MEANS ONE THING ONLY.
 *
 * Somebody measured it at the house, and said so. A calibrated number is a
 * better guess than an estimate and it is still a guess: the reference
 * dimension it was scaled from was itself typed by a person looking at a
 * photograph. Treating the two the same is how a bid goes out forty feet
 * short.
 */
export const isVerified = (m: Measurement): boolean =>
  m.source === 'field' && !!m.verifiedAt

/** Reads as a sentence under a number, so nobody has to learn the words. */
export function measurementWording(m: Measurement): string {
  if (isVerified(m)) {
    const how = m.evidence.trim()
    return how ? `Measured at the house, ${how}` : 'Measured at the house'
  }
  if (m.source === 'field') return 'Field measurement, not confirmed'
  if (m.source === 'calibrated') return 'Scaled from a reference dimension'
  return 'Estimated from the photo'
}

/** Plus or minus, where somebody gave a range. "38 ft ± 4". */
export function measurementText(m: Measurement): string {
  const feet = Math.round(m.feet * 10) / 10
  return m.plusMinus > 0 ? `${feet} ft ± ${m.plusMinus}` : `${feet} ft`
}

let seq = 0
/**
 * A section reference. Random plus a counter, because two sections drawn in
 * the same millisecond on the same phone must not collide, and because the id
 * has to survive being copied to another device.
 */
export const newSectionId = (): string =>
  `sec_${Date.now().toString(36)}${(seq++).toString(36)}${Math.random().toString(36).slice(2, 7)}`

export function createSection(patch: Partial<RoofSection> = {}): RoofSection {
  /*
   * The id is minted here and nowhere else, and a caller may hand one in only
   * when it is restoring a section that already exists. Nothing derives it
   * from the label or from a position in the array.
   */
  const { id, ...rest } = patch
  return {
    id: id ?? newSectionId(),
    label: '',
    kind: 'other',
    runIds: [],
    geometryRev: 1,
    measurement: emptyMeasurement(),
    geometrySig: '',
    history: [],
    excluded: false,
    excludedReason: '',
    ...rest,
  }
}

/**
 * RECORD A LENGTH SOMEBODY ACTUALLY MEASURED.
 *
 * The only path to a verified state. It takes the actor and the time from the
 * caller rather than reading a clock, so a field entry made offline keeps the
 * device time it was taken at and a test can be deterministic.
 */
export function verifyMeasurement(
  s: RoofSection,
  input: { feet: number; by: string; at: string; evidence?: string; reason?: string },
): RoofSection {
  return {
    ...s,
    history: pushOverride(s, { at: input.at, by: input.by, reason: input.reason ?? '' }),
    measurement: {
      feet: Math.max(0, input.feet),
      source: 'field',
      // A measured length is a measured length. Any range it used to carry
      // described the guess it replaced.
      plusMinus: 0,
      verifiedAt: input.at,
      verifiedBy: input.by,
      evidence: input.evidence ?? '',
    },
  }
}

/**
 * A LENGTH THAT IS NOT A FIELD MEASUREMENT.
 *
 * Typed off the photo, or scaled from the reference dimension somebody already
 * entered on it. Deliberately cannot produce a verified state - there is no
 * argument to this function that would let it - so no future caller can reach
 * verified by passing the right flag.
 */
export function setMeasurement(
  s: RoofSection,
  input: {
    feet: number
    source: 'estimated' | 'calibrated'
    plusMinus?: number
    at: string
    by?: string
    reason?: string
  },
): RoofSection {
  return {
    ...s,
    history: pushOverride(s, { at: input.at, by: input.by ?? '', reason: input.reason ?? '' }),
    measurement: {
      feet: Math.max(0, input.feet),
      source: input.source,
      plusMinus: Math.max(0, input.plusMinus ?? 0),
      verifiedAt: null,
      verifiedBy: '',
      evidence: '',
    },
  }
}

/**
 * Records the OUTGOING measurement, and only when there was one. A section
 * that has never carried a length has nothing to remember, and an entry saying
 * it used to be zero feet estimated is noise in the one place that has to stay
 * readable.
 */
function pushOverride(
  s: RoofSection,
  who: { at: string; by: string; reason: string },
): MeasureOverride[] {
  if (s.measurement.feet <= 0) return s.history
  return [...s.history, { at: who.at, by: who.by, reason: who.reason, was: s.measurement }]
}

/**
 * THE SHAPE CHANGED, SO THE VERIFICATION IS ABOUT SOMETHING ELSE NOW.
 *
 * Called whenever the runs under a section are redrawn. It does not throw the
 * number away - the length somebody measured is still the best figure anybody
 * has - it demotes it to a field number nobody has confirmed against the new
 * shape, and bumps the revision so every derived quantity knows to recompute.
 *
 * Losing the number outright was the other option and it is worse: a crew that
 * loses a measurement because an estimator nudged a corner will stop measuring.
 */
export function geometryChanged(s: RoofSection): RoofSection {
  if (!isVerified(s.measurement)) {
    return { ...s, geometryRev: s.geometryRev + 1 }
  }
  return {
    ...s,
    geometryRev: s.geometryRev + 1,
    measurement: { ...s.measurement, verifiedAt: null },
  }
}

/** Renaming is not identity. Here so the rule is somewhere it can be tested. */
export const renameSection = (s: RoofSection, label: string): RoofSection => ({
  ...s,
  label,
})

export interface SectionTotals {
  /** Every section that is in the job, whatever anybody knows about it. */
  included: number
  /** Feet across included sections. */
  feet: number
  /** Feet nobody has confirmed at the house. The number the gate is about. */
  unverifiedFeet: number
  /** Included sections with no length on them at all. */
  missing: number
  /** True when every included section has been measured at the house. */
  allVerified: boolean
}

/**
 * WHAT THE ESTIMATE IS ALLOWED TO SAY.
 *
 * Excluded sections are not counted anywhere: a section somebody deliberately
 * left out is not quiet revenue waiting to be found. And the unverified figure
 * is reported alongside the total rather than folded into it, so a screen can
 * show what is still a guess instead of presenting one number that pretends
 * the whole house has been walked.
 */
export function sectionTotals(sections: RoofSection[]): SectionTotals {
  const included = sections.filter((s) => !s.excluded)
  let feet = 0
  let unverifiedFeet = 0
  let missing = 0
  for (const s of included) {
    feet += s.measurement.feet
    if (!isVerified(s.measurement)) unverifiedFeet += s.measurement.feet
    if (s.measurement.feet <= 0) missing++
  }
  const round = (n: number) => Math.round(n * 100) / 100
  return {
    included: included.length,
    feet: round(feet),
    unverifiedFeet: round(unverifiedFeet),
    missing,
    allVerified: included.length > 0 && unverifiedFeet === 0 && missing === 0,
  }
}

/**
 * Every section on this document, in a shape that cannot be undefined.
 *
 * Documents saved before sections existed have no field at all, and a missing
 * array here is a crash on a screen somebody opened to look at a job from last
 * season. One place to be defensive about it.
 */
export const sectionsOf = (d: { sections?: RoofSection[] }): RoofSection[] =>
  Array.isArray(d.sections) ? d.sections : []

/**
 * ATTACH A DRAWN RUN TO A PLACE ON THE HOUSE.
 *
 * Idempotent, and a run belongs to at most one section: attaching it here
 * detaches it everywhere else. Two sections both claiming the same run is how
 * one fascia gets billed twice, and no screen can be trusted to prevent it.
 */
export function attachRun(sections: RoofSection[], sectionId: string, runId: string): RoofSection[] {
  return sections.map((s) => {
    if (s.id === sectionId) {
      return s.runIds.includes(runId) ? s : { ...s, runIds: [...s.runIds, runId] }
    }
    return s.runIds.includes(runId) ? { ...s, runIds: s.runIds.filter((r) => r !== runId) } : s
  })
}

export function detachRun(sections: RoofSection[], runId: string): RoofSection[] {
  return sections.map((s) =>
    s.runIds.includes(runId) ? { ...s, runIds: s.runIds.filter((r) => r !== runId) } : s,
  )
}

/** Which section owns this run, if any. One at most, by construction above. */
export const sectionOfRun = (sections: RoofSection[], runId: string): RoofSection | null =>
  sections.find((s) => s.runIds.includes(runId)) ?? null

/**
 * WHAT THE SHAPE UNDER A SECTION LOOKS LIKE RIGHT NOW.
 *
 * Coordinates are normalized 0..1, so three decimals is finer than a pixel on
 * any photograph anyone will upload and coarse enough that a re-render or a
 * float rounding does not read as somebody moving the roof.
 */
export function geometrySignature(
  runIds: string[],
  runs: { id: string; points?: { x: number; y: number }[] }[],
): string {
  const parts: string[] = []
  for (const id of [...runIds].sort()) {
    const run = runs.find((r) => r.id === id)
    if (!run) {
      // A missing run is itself a geometry change, and saying so is the point.
      parts.push(`${id}:gone`)
      continue
    }
    const pts = (run.points ?? []).map((p) => `${p.x.toFixed(3)},${p.y.toFixed(3)}`).join(' ')
    parts.push(`${id}:${pts}`)
  }
  return parts.join('|')
}

/**
 * BRING EVERY SECTION UP TO DATE WITH THE DRAWING, ON ANY CODE PATH.
 *
 * Runs on the way into the store, which is why no caller has to remember it.
 * Three things happen, all of them pure:
 *
 * 1. A run that no longer exists stops being referenced. A deleted layer must
 *    not leave a section pointing at a ghost.
 * 2. A section whose shape moved gets a new revision, and a verification is
 *    demoted, because it described the old shape.
 * 3. Nothing else. It never edits a length, never excludes anything and never
 *    creates or destroys a section.
 *
 * Returns the same array when nothing changed, so React and the undo stack see
 * no work where there was none.
 */
export function syncSections<T extends { id: string; points?: { x: number; y: number }[] }>(
  sections: RoofSection[],
  runs: T[],
): RoofSection[] {
  if (sections.length === 0) return sections
  const live = new Set(runs.map((r) => r.id))
  let changed = false

  const next = sections.map((s) => {
    const kept = s.runIds.filter((id) => live.has(id))
    const dropped = kept.length !== s.runIds.length
    const sig = geometrySignature(kept, runs)
    if (!dropped && sig === s.geometrySig) return s

    changed = true
    /*
     * A section that has never been attached to anything is not "changed" the
     * first time it is looked at: its stored signature is empty and so is the
     * computed one, which the equality above already caught. Getting here
     * means real geometry moved, so the revision moves with it.
     */
    const bumped = geometryChanged({ ...s, runIds: kept })
    return { ...bumped, geometrySig: sig }
  })

  return changed ? next : sections
}

/**
 * Feet the drawing itself can account for, per section, when a scale exists.
 * Returns null when the photo has never been calibrated, because a pixel
 * length without a scale is not a length.
 */
export function suggestedFeet(
  section: RoofSection,
  perStrandFeet: { id: string; feet: number | null }[],
): number | null {
  let total = 0
  let any = false
  for (const id of section.runIds) {
    const m = perStrandFeet.find((x) => x.id === id)
    if (!m || m.feet === null) continue
    total += m.feet
    any = true
  }
  return any ? Math.round(total * 10) / 10 : null
}

/** One line saying what still stands between this job and an approvable scope. */
export function gateWording(t: SectionTotals): string | null {
  if (t.included === 0) return 'No sections yet. Add one for each part of the roof.'
  if (t.missing > 0) {
    return t.missing === 1
      ? '1 section has no length on it.'
      : `${t.missing} sections have no length on them.`
  }
  if (t.unverifiedFeet > 0) {
    return `${Math.round(t.unverifiedFeet)} ft has not been measured at the house.`
  }
  return null
}
